import type { Component } from 'vue';
import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/vue-3';
import { VueNodeViewRenderer, VueRenderer } from '@tiptap/vue-3';
import { PluginKey } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { emojis, type EmojiItem } from '@tiptap/extension-emoji';
import {
  Suggestion,
  exitSuggestion,
  SuggestionPluginKey,
  type SuggestionKeyDownProps,
  type SuggestionProps,
} from '@tiptap/suggestion';
import { searchService } from '@/api/search.service';
import {
  commentsService,
  type CommentMentionCandidate,
  type CommentableEntityType,
} from '@/api/comments.service';
import {
  entityLinkHref,
  flattenEntitySearchResults,
  type EntityLinkItem,
} from './comment-entity-link';
import { customEmojis, getCustomEmoji } from './comment-custom-emojis';
import { CustomEmoji } from './comment-custom-emoji-node';
import CommentEntityLinkList from './CommentEntityLinkList.vue';
import CommentEntityLinkNode from './CommentEntityLinkNode.vue';
import CommentEmojiList from './CommentEmojiList.vue';
import CommentMentionList from './CommentMentionList.vue';

export const entityLinkSuggestionKey = new PluginKey('entityLink');
export const emojiSuggestionKey = new PluginKey('emojiSuggestion');

const EMOJI_LIMIT = 8;

const toCustomEmojiItem = (emoji: (typeof customEmojis)[number]): EmojiItem =>
  ({
    name: emoji.name,
    shortcodes: [emoji.name],
    tags: [],
    group: 'custom',
    emoticons: [],
    version: 0,
    emoji: emoji.shortcode,
    fallbackImage: emoji.src,
  }) as EmojiItem;

const filterEmojis = (query: string): EmojiItem[] => {
  const q = query.toLowerCase();
  if (!q) return [];

  const customMatches = customEmojis
    .filter((item) => item.name.startsWith(q) || item.name.includes(q))
    .map(toCustomEmojiItem);

  const prefix: EmojiItem[] = [];
  const rest: EmojiItem[] = [];
  for (const item of emojis) {
    if (!item.emoji) continue;
    if (item.name.startsWith(q) || item.shortcodes.some((code) => code.startsWith(q))) {
      prefix.push(item);
    } else if (item.tags.some((tag) => tag.startsWith(q))) {
      rest.push(item);
    }
  }
  return [...customMatches, ...prefix, ...rest].slice(0, EMOJI_LIMIT);
};

type SuggestionList = {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
};

const createSuggestionPopup = <T>(
  component: Component,
  extraProps: (props: SuggestionProps<T>) => Record<string, unknown>,
  pluginKey: PluginKey,
) => {
  return () => {
    let renderer: VueRenderer | null = null;
    let popup: HTMLDivElement | null = null;

    const place = (props: SuggestionProps<T>) => {
      const rect = props.clientRect?.();
      if (!popup || !rect) return;

      const gap = 4;
      const height = popup.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const openUp = height > 0 && spaceBelow < height && rect.top - gap > spaceBelow;

      popup.style.left = `${Math.min(rect.left, window.innerWidth - popup.offsetWidth - gap)}px`;
      popup.style.top = openUp ? `${rect.top - gap - height}px` : `${rect.bottom + gap}px`;
    };

    return {
      onStart: (props: SuggestionProps<T>) => {
        renderer = new VueRenderer(component, {
          editor: props.editor as Editor,
          props: extraProps(props),
        });
        popup = document.createElement('div');
        popup.dataset.commentsOverlay = '';
        popup.style.position = 'fixed';
        // Above vue-final-modal (1000 + 2 * index) and command palette (9999).
        popup.style.zIndex = '10050';
        if (renderer.element) {
          popup.appendChild(renderer.element);
        }
        document.body.appendChild(popup);
        place(props);
        // Height is only known after the list paints.
        requestAnimationFrame(() => place(props));
      },
      onUpdate: (props: SuggestionProps<T>) => {
        renderer?.updateProps(extraProps(props));
        place(props);
        requestAnimationFrame(() => place(props));
      },
      onKeyDown: (props: SuggestionKeyDownProps) => {
        if (props.event.key === 'Escape') {
          exitSuggestion(props.view, pluginKey);
          return true;
        }
        return (renderer?.ref as SuggestionList | null)?.onKeyDown(props) ?? false;
      },
      onExit: () => {
        popup?.remove();
        popup = null;
        renderer?.destroy();
        renderer = null;
      },
    };
  };
};

export const createCommentExtensions = (options: {
  editable: boolean;
  entityId: string;
  entityType: CommentableEntityType;
  noMatches: string;
}) => [
  StarterKit.configure({
    heading: false,
    blockquote: false,
    codeBlock: false,
    code: false,
    strike: false,
    underline: false,
    horizontalRule: false,
    link: {
      openOnClick: !options.editable,
      autolink: true,
      defaultProtocol: 'https',
      protocols: ['http', 'https'],
      HTMLAttributes: options.editable ? { tabindex: '-1' } : {},
    },
  }),
  Mention.configure({
    HTMLAttributes: { class: 'mention' },
    renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
    suggestion: {
      char: '@',
      pluginKey: SuggestionPluginKey,
      items: async ({ query }: { query: string }) => {
        if (!options.editable) return [];
        return commentsService.mentionCandidates(
          { entityType: options.entityType, entityId: options.entityId },
          query,
        );
      },
      render: createSuggestionPopup<CommentMentionCandidate>(
        CommentMentionList,
        (props) => ({
          items: props.items,
          command: props.command,
          emptyText: options.noMatches,
        }),
        SuggestionPluginKey,
      ),
    },
  }),
  Mention.extend({
    name: 'entityLink',
    addOptions() {
      const parent = this.parent?.();
      return {
        ...parent,
        deleteTriggerWithBackspace: parent?.deleteTriggerWithBackspace ?? false,
        suggestions: parent?.suggestions ?? [],
        HTMLAttributes: { class: 'entity-link' },
        renderText: ({ node }) => String(node.attrs.label ?? node.attrs.id ?? ''),
        renderHTML: ({ options: mentionOptions, node }) => {
          const href = options.editable
            ? null
            : entityLinkHref(node.attrs.entityType, node.attrs.id);
          return [
            'a',
            {
              ...mentionOptions.HTMLAttributes,
              ...(options.editable ? { tabindex: '-1' } : {}),
              ...(href ? { href } : {}),
            },
            String(node.attrs.label ?? node.attrs.id ?? ''),
          ];
        },
        suggestion: {
          ...parent?.suggestion,
          char: '#',
          pluginKey: entityLinkSuggestionKey,
          allowSpaces: true,
          items: async ({ query }: { query: string }) => {
            if (!options.editable) return [];
            const q = query.trim();
            if (!q) return [];
            try {
              return flattenEntitySearchResults(await searchService.search(q));
            } catch {
              return [];
            }
          },
          render: createSuggestionPopup<EntityLinkItem>(
            CommentEntityLinkList,
            (props) => ({
              items: props.items,
              command: props.command,
              query: props.query,
            }),
            entityLinkSuggestionKey,
          ),
        },
      };
    },
    addAttributes() {
      return {
        ...this.parent?.(),
        mentionSuggestionChar: {
          default: '#',
          parseHTML: (element) => element.getAttribute('data-mention-suggestion-char'),
          renderHTML: (attributes) => ({
            'data-mention-suggestion-char': attributes.mentionSuggestionChar,
          }),
        },
        entityType: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-entity-type'),
          renderHTML: (attributes) =>
            attributes.entityType ? { 'data-entity-type': attributes.entityType } : {},
        },
      };
    },
    parseHTML() {
      return [{ tag: `a[data-type="${this.name}"]` }];
    },
    addNodeView() {
      return VueNodeViewRenderer(CommentEntityLinkNode);
    },
  }),
  CustomEmoji,
  Extension.create({
    name: 'emojiSuggestion',
    addProseMirrorPlugins() {
      return [
        Suggestion<EmojiItem, { emoji: string }>({
          editor: this.editor,
          char: ':',
          pluginKey: emojiSuggestionKey,
          items: ({ query }) => (options.editable ? filterEmojis(query) : []),
          shouldShow: ({ query }) => options.editable && query.length > 0,
          command: ({ editor, range, props }) => {
            if (!props.emoji) return;
            const nodeAfter = editor.view.state.selection.$to.nodeAfter;
            if (nodeAfter?.text?.startsWith(' ')) {
              range.to += 1;
            }
            const custom = getCustomEmoji(props.emoji);
            if (custom) {
              editor
                .chain()
                .focus()
                .insertContentAt(range, [
                  { type: 'customEmoji', attrs: { name: custom.name } },
                  { type: 'text', text: ' ' },
                ])
                .run();
              return;
            }
            editor.chain().focus().insertContentAt(range, `${props.emoji} `).run();
          },
          render: createSuggestionPopup<EmojiItem>(
            CommentEmojiList,
            (props) => ({
              items: props.items,
              command: props.command,
            }),
            emojiSuggestionKey,
          ),
        }),
      ];
    },
  }),
];
